import { showToast } from "../../utils/toast";
import { replaceRow, sendJson } from "./request";
import { refreshList } from "./refresh";

type Option = [string, string, string?];
type Options = Record<string, Option[]> & { labels?: Record<string, string> };

const INPUT_FIELDS: Record<string, string> = { mrr: "number", renews: "date" };
let cell: HTMLElement | null = null;

const popover = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-cs-popover]");

function editOptions(): Options {
  try {
    return JSON.parse(document.getElementById("cs-edit-options")?.textContent || "{}");
  } catch (_e) {
    return {} as Options;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function close(): void {
  const pop = popover();
  if (pop) {
    pop.hidden = true;
    pop.innerHTML = "";
  }
  cell?.classList.remove("is-editing");
  cell = null;
}

function place(pop: HTMLElement, td: HTMLElement): void {
  const rect = td.getBoundingClientRect();
  pop.style.left = `${Math.min(rect.left, window.innerWidth - pop.offsetWidth - 12)}px`;
  const below = rect.bottom + 4;
  pop.style.top = below + pop.offsetHeight > window.innerHeight - 12 ? `${Math.max(12, rect.top - pop.offsetHeight - 4)}px` : `${below}px`;
}

function head(title: string): string {
  return `<div class="cs-pop-head">${escapeHtml(title)}<button type="button" class="x-btn" data-cs-pop-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>`;
}

function render(field: string, current: any, options: Options): string {
  const title = options.labels?.[field] || field;
  if (INPUT_FIELDS[field]) {
    const step = field === "mrr" ? ' step="1" min="0"' : "";
    return `${head(title)}<form class="cs-pop-body" data-cs-pop-input>
      <input class="cs-pop-input" type="${INPUT_FIELDS[field]}" name="value" value="${escapeHtml(String(current ?? ""))}"${step}>
      <div class="cs-pop-foot"><button type="button" class="btn btn-sm" data-cs-pop-close>Cancel</button><button type="submit" class="btn btn-sm btn-primary">Save</button></div>
    </form>`;
  }
  if (field === "tags") {
    const chosen: string[] = current || [];
    const items = (options.tags || []).map(([value, label]) =>
      `<label class="dd-opt ${chosen.includes(value) ? "active" : ""}"><input type="checkbox" value="${value}" ${chosen.includes(value) ? "checked" : ""}><span class="tick"></span><span class="dd-opt-label">${escapeHtml(label)}</span></label>`).join("");
    return `${head(title)}<form data-cs-pop-tags>${items}
      <div class="cs-pop-body"><div class="cs-pop-foot"><button type="button" class="btn btn-sm" data-cs-pop-close>Cancel</button><button type="submit" class="btn btn-sm btn-primary">Save</button></div></div></form>`;
  }
  const value = current === null || current === undefined ? "" : String(current);
  const items = (options[field] || []).map(([optValue, label, swatch]) =>
    `<button type="button" class="cs-pop-opt ${optValue === value ? "active" : ""}" data-cs-pop-value="${escapeHtml(optValue)}">${swatch ? `<span class="sw" style="background:${swatch}"></span>` : ""}${escapeHtml(label)}</button>`).join("");
  return `${head(title)}<div class="cs-pop-list">${items}</div>`;
}

async function save(field: string, value: unknown): Promise<void> {
  const td = cell;
  const row = td?.closest<HTMLElement>("tr.customer-row");
  if (!td || !row?.dataset.id) return;

  close();
  td.classList.add("is-saving");
  const label = editOptions().labels?.[field] || field;
  const { ok, data } = await sendJson(`/customer_success/update_field?id=${row.dataset.id}&row=1`, "PATCH", JSON.stringify({ field, value }));
  td.classList.remove("is-saving");
  if (!ok) {
    showToast(data.message || `Couldn't save ${label}`, "error", 3000);
    return;
  }
  replaceRow(row.dataset.id, data.row_html);
  showToast(`Saved ${label} · ${row.dataset.name || ""}`);
  void refreshList();
}

function open(td: HTMLElement): void {
  const pop = popover();
  const row = td.closest<HTMLElement>("tr.customer-row");
  const field = td.dataset.edit;
  if (!pop || !row || !field) return;

  if (cell === td) {
    close();
    return;
  }
  close();
  cell = td;
  td.classList.add("is-editing");
  const values = JSON.parse(row.dataset.values || "{}");
  pop.innerHTML = render(field, values[field], editOptions());
  pop.hidden = false;
  place(pop, td);
  pop.querySelector<HTMLInputElement>(".cs-pop-input")?.focus();
}

export function bindCsCellPopover(): void {
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const pop = popover();
    if (!pop) return;

    if (target.closest("[data-cs-pop-close]")) return close();

    const option = target.closest<HTMLElement>("[data-cs-pop-value]");
    if (option && cell?.dataset.edit) {
      const field = cell.dataset.edit;
      const raw = option.dataset.csPopValue || "";
      void save(field, field === "churn_risk" ? raw === "true" : raw);
      return;
    }

    const td = target.closest<HTMLElement>("[data-cs-grid] td[data-edit]");
    if (td && !target.closest("a")) {
      open(td);
      return;
    }
    if (!pop.hidden && !target.closest("[data-cs-popover]")) close();
  });

  document.addEventListener("change", (e) => {
    const box = e.target as HTMLInputElement;
    if (box.closest("[data-cs-pop-tags]")) box.closest(".dd-opt")?.classList.toggle("active", box.checked);
  });

  document.addEventListener("submit", (e) => {
    const form = e.target as HTMLFormElement;
    if (!cell?.dataset.edit) return;
    if (form.matches("[data-cs-pop-input]")) {
      e.preventDefault();
      void save(cell.dataset.edit, (form.elements.namedItem("value") as HTMLInputElement).value);
    } else if (form.matches("[data-cs-pop-tags]")) {
      e.preventDefault();
      const tags = Array.from(form.querySelectorAll<HTMLInputElement>("input:checked")).map((box) => box.value);
      void save(cell.dataset.edit, tags);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && cell) close();
  });
  // Scrolling the table keeps the popover on its cell (as the mockup does); any other scroll closes it.
  document.addEventListener("scroll", (e) => {
    const target = e.target as HTMLElement;
    if (!cell || target.closest?.("[data-cs-popover]")) return;
    const pop = popover();
    if (pop && target.matches?.("[data-cs-grid]")) place(pop, cell);
    else close();
  }, true);
}
